import React from "react";
import { View, Image, type ImageSourcePropType } from "react-native";
import type { Clinic } from "@louise/clinics/clinics.types";
import { employeeBenefitOptions } from "@louise/clinics/clinics.types";
import { SectionTitle, Tag } from "@louise/moon-ui";
import ClinicInfoEmptyState from "./ClinicInfoEmptyState";
import { useTranslations } from "@src/packages/i18n";

const CARRIER_LOGOS: Record<string, ImageSourcePropType> = {
  aetna:
    require("../../../assets/images/insurance/aetna.png") as ImageSourcePropType,
  anthem:
    require("../../../assets/images/insurance/anthem.png") as ImageSourcePropType,
  "anthem blue cross":
    require("../../../assets/images/insurance/anthem.png") as ImageSourcePropType,
  bcbs: require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue cross":
    require("../../../assets/images/insurance/bluecross.png") as ImageSourcePropType,
  "blue cross (anthem)":
    require("../../../assets/images/insurance/bluecross.png") as ImageSourcePropType,
  "blue cross blue shield":
    require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue cross blue shield of illinois":
    require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue cross blue shield of massachusetts":
    require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue cross blue shield of rhode island":
    require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue cross blue shield ppo (out-of-state)":
    require("../../../assets/images/insurance/bcbs.png") as ImageSourcePropType,
  "blue shield":
    require("../../../assets/images/insurance/blueshield.png") as ImageSourcePropType,
  "blue shield of ca":
    require("../../../assets/images/insurance/blueshield.png") as ImageSourcePropType,
  "blue shield of california":
    require("../../../assets/images/insurance/blueshield.png") as ImageSourcePropType,
  cigna:
    require("../../../assets/images/insurance/cigna.png") as ImageSourcePropType,
  empire:
    require("../../../assets/images/insurance/empire.png") as ImageSourcePropType,
  "health net":
    require("../../../assets/images/insurance/healthnet.png") as ImageSourcePropType,
  healthnet:
    require("../../../assets/images/insurance/healthnet.png") as ImageSourcePropType,
  horizon:
    require("../../../assets/images/insurance/horizon.png") as ImageSourcePropType,
  humana:
    require("../../../assets/images/insurance/humana.png") as ImageSourcePropType,
  kaiser:
    require("../../../assets/images/insurance/kaiser.png") as ImageSourcePropType,
  "kaiser permanente":
    require("../../../assets/images/insurance/kaiser.png") as ImageSourcePropType,
  medicaid:
    require("../../../assets/images/insurance/medicaid.png") as ImageSourcePropType,
  medicare:
    require("../../../assets/images/insurance/medicare.png") as ImageSourcePropType,
  "medicare advantage":
    require("../../../assets/images/insurance/medicare.png") as ImageSourcePropType,
  molina:
    require("../../../assets/images/insurance/molina.png") as ImageSourcePropType,
  optum:
    require("../../../assets/images/insurance/optum.png") as ImageSourcePropType,
  oscar:
    require("../../../assets/images/insurance/oscar.png") as ImageSourcePropType,
  oxford:
    require("../../../assets/images/insurance/oxford.png") as ImageSourcePropType,
  tricare:
    require("../../../assets/images/insurance/tricare.png") as ImageSourcePropType,
  "united healthcare":
    require("../../../assets/images/insurance/uhc.png") as ImageSourcePropType,
  unitedhealthcare:
    require("../../../assets/images/insurance/uhc.png") as ImageSourcePropType,
};

// Employer-sponsored fertility benefit programs. Keyed by the benefit name
// normalized to lowercase alphanumerics (so "WIN Fertility" and "WINFertility"
// both resolve). Names without a logo fall back to a plain text chip.
const BENEFIT_LOGOS: Record<string, ImageSourcePropType> = {
  progyny:
    require("../../../assets/images/benefits/progyny.png") as ImageSourcePropType,
  carrot:
    require("../../../assets/images/benefits/carrot.png") as ImageSourcePropType,
  maven:
    require("../../../assets/images/benefits/maven.png") as ImageSourcePropType,
  winfertility:
    require("../../../assets/images/benefits/winfertility.png") as ImageSourcePropType,
  kindbody:
    require("../../../assets/images/benefits/kindbody.png") as ImageSourcePropType,
};

function benefitLogo(name: string): ImageSourcePropType | undefined {
  return BENEFIT_LOGOS[name.toLowerCase().replace(/[^a-z0-9]/g, "")];
}

/** The carrier or benefit logo leading a chip, when there is one. */
function ChipLogo({ source }: { source: ImageSourcePropType | undefined }) {
  if (!source) return null;
  return (
    <Image
      source={source}
      style={{ width: 16, height: 16, borderRadius: 3 }}
      resizeMode="contain"
    />
  );
}

type Props = {
  clinic: Clinic;
};

export function hasClinicInsuranceDetails(clinic: Clinic): boolean {
  return (
    (clinic.insuranceProviders?.length ?? 0) > 0 ||
    (clinic.employeeBenefits?.length ?? 0) > 0
  );
}

export default function ClinicInsuranceCard({ clinic }: Props) {
  const t = useTranslations();
  const insuranceNames = clinic.insuranceProviders ?? [];
  const employeeBenefits = clinic.employeeBenefits || [];

  const displayBenefits = employeeBenefits.map(
    (benefit) =>
      employeeBenefitOptions.find((opt) => opt.id === benefit)?.label ||
      benefit,
  );

  const hasInsurance = insuranceNames.length > 0;
  const hasBenefits = displayBenefits.length > 0;

  // Keep the insurance block visible even with no data, so the cost section
  // never silently loses a category.
  if (!hasClinicInsuranceDetails(clinic)) {
    return (
      <ClinicInfoEmptyState
        message={t("access.clinic_detail.empty.insurance")}
      />
    );
  }

  return (
    <View style={{ gap: 14 }}>
      <SectionTitle level="subsection">
        {t("access.clinic_detail.section.accepted_insurance")}
      </SectionTitle>

      {hasInsurance && (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {insuranceNames.map((insurance, index) => (
            <Tag
              key={index}
              variant="outline"
              label={insurance}
              leading={
                <ChipLogo source={CARRIER_LOGOS[insurance.toLowerCase()]} />
              }
            />
          ))}
        </View>
      )}

      {hasBenefits && (
        <View style={{ gap: 8 }}>
          <SectionTitle level="subsection">
            {t("access.clinic_detail.section.employer_benefits")}
          </SectionTitle>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {displayBenefits.map((benefit, index) => (
              <Tag
                key={index}
                variant="outline"
                label={benefit}
                leading={<ChipLogo source={benefitLogo(benefit)} />}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
}
